/**
 * useAnchorNav — 统一锚点跳转入口
 *
 * 解决两个场景：
 * 1. 同页 hash 切换（已在 /，点 /#features）：scrollIntoView + history.replaceState
 * 2. 跨页 hash 跳转（在 /privacy，点 /#features）：router.push(path) → 轮询等 DOM
 *    命中后 history.replaceState 注 hash + scrollIntoView，不传 hash 给 router
 *    是为了避免与 router scrollBehavior 兜底重复触发滚动
 *
 * 见 docs/mainadr/0008-anchor-navigation.md
 */
import { useRoute, useRouter } from 'vue-router'

const POLL_INTERVAL_MS = 50
const POLL_MAX_ATTEMPTS = 10 // 500ms 上限

function smoothScrollTo(el: Element) {
  // CSS scroll-margin-top 已在 Home 各 section 上挂上，scrollIntoView 自然避开 sticky header
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function pollForElement(id: string): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    let attempts = 0
    const tick = () => {
      const el = document.getElementById(id)
      if (el) {
        resolve(el)
        return
      }
      attempts += 1
      if (attempts >= POLL_MAX_ATTEMPTS) {
        resolve(null)
        return
      }
      requestAnimationFrame(() => setTimeout(tick, POLL_INTERVAL_MS))
    }
    tick()
  })
}

export function useAnchorNav() {
  const router = useRouter()
  const route = useRoute()

  /**
   * 处理锚点跳转。同页 / 跨页都走这个函数。
   * @param href 形如 "/#features" "/install" "/privacy"
   * @param onAfterNavigate 跳转后的 UI 副作用（SiteHeader 用来关 drawer）
   */
  async function go(href: string, onAfterNavigate?: () => void) {
    // 站内：非 hash 普通路径（/install /about /privacy /terms）
    if (!href.startsWith('/#')) {
      if (route.path !== href) {
        await router.push(href)
      }
      onAfterNavigate?.()
      return
    }

    const id = href.slice(2)
    const isOnHome = route.path === '/'

    if (isOnHome) {
      // 同页 hash 切换：DOM 必然存在（路由没换），直接滚
      const el = document.getElementById(id)
      if (el) {
        smoothScrollTo(el)
        history.replaceState(null, '', `/#${id}`)
      }
      onAfterNavigate?.()
      return
    }

    // 跨页：router.push 不传 hash，让 scrollBehavior 兜底看不见这次跳转
    // 路由切换完成后我们自己负责滚动 + 注 hash
    await router.push('/')
    onAfterNavigate?.()
    const el = await pollForElement(id)
    if (el) {
      smoothScrollTo(el)
      history.replaceState(null, '', `/#${id}`)
    }
    // 500ms 还没找到 element：放弃（可能 href 写错了，不抛错避免白屏）
  }

  return { go }
}
